"use client";

import { pagePath } from "@repo/utils/lib/seo-pagination";

import { AgentListBreadcrumbs } from "@/app/(saas)/(app)/business-brokers/_components/agent-list-breadcrumbs";
import { AgentListItem } from "@/app/(saas)/(app)/business-brokers/_components/agent-list-item";
import { AgentListItemSkeleton } from "@/app/(saas)/(app)/business-brokers/_components/agent-list-item-skeleton";
import { AgentListZeroDataState } from "@/app/(saas)/(app)/business-brokers/_components/agent-list-zero-data-state";
import { AgentListFilters } from "@/app/(saas)/(app)/business-brokers/_components/filters/agent-list-filters";
import {
  type AgentFilters,
  agentsFiltersAtom,
  readFromAgentSearchParams,
  getAgentSearchParams,
} from "@/app/(saas)/(app)/business-brokers/_components/filters/state";
import { Pagination } from "@/components/common/pagination";
import { api } from "@/lib/api-client";
import { formatLocation } from "@/lib/locations";
import type { ApiOutput } from "@repo/api/trpc/router";
import type { SearchHookLocation } from "@repo/maps";
import {
  getCountryDisplayName,
  getPostcodeFromLocationName,
} from "@repo/countries";
import { useAtom } from "jotai";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

export type AgentsGetOutput = ApiOutput["agents"]["get"];

export type AgentListViewProps = {
  countryCode?: string;
  location?: SearchHookLocation;
  initialData?: AgentsGetOutput;
  initialPage?: number;
  basePath?: string;
};

export function AgentListView({
  countryCode,
  location,
  initialData,
  initialPage = 1,
  basePath,
}: AgentListViewProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [storedFilters, setFilters] = useAtom(agentsFiltersAtom);
  const [ready, setReady] = useState(false);
  const filters = ready
    ? storedFilters
    : {
        ...storedFilters,
        ...readFromAgentSearchParams(
          new URLSearchParams(searchParams.toString()),
        ),
        page: initialPage,
        location: location ?? null,
      };

  const { data, isLoading, isFetching } = api.agents.get.useQuery(
    {
      name: filters.name ?? undefined,
      languageIds: filters.languageIds,
      specialtyIds: filters.specialtyIds,
      countryCode,
      neighborhood: filters.location?.context.neighborhood?.name,
      city: filters.location?.context.place?.name,
      county: filters.location?.context.district?.name,
      state: filters.location?.context.region?.name,
      zipCode: getPostcodeFromLocationName(
        countryCode ?? "",
        filters.location?.name,
      ),
      sort: filters.sort,
      page: filters.page,
    },
    {
      initialData,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  function setPage(page: number) {
    setFilters((prev) => ({ ...prev, page }));
  }

  const debouncedSearchParamsUpdate = useDebouncedCallback(
    (
      pathname: string,
      currentParams: URLSearchParams,
      filters: AgentFilters,
    ) => {
      const params = new URLSearchParams(currentParams.toString());
      const updated = getAgentSearchParams(filters, params);
      if (basePath) updated.delete("page");
      const query = updated.toString();
      window.history.replaceState(
        null,
        "",
        `${basePath ? pagePath(basePath, filters.page) : pathname}${query ? `?${query}` : ""}`,
      );
    },
    100,
  );

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    if (ready) debouncedSearchParamsUpdate(pathname, params, filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, location: location ?? null }));
  }, [location]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    const filters = readFromAgentSearchParams(params);
    setFilters((prev) => ({
      ...prev,
      ...filters,
      page: initialPage,
      location: location ?? null,
    }));
    setReady(true);
  }, []);

  const formattedLocation = location
    ? formatLocation(location)
    : countryCode
      ? (getCountryDisplayName(countryCode) ?? null)
      : null;

  const showingStart = data ? (data.page - 1) * data.limit + 1 : 0;
  const showingEnd = data ? Math.min(data.page * data.limit, data.total) : 0;
  const visibleAgents = data
    ? data.agents.filter((agent) => agent.isVerified)
    : [];

  return (
    <section className="relative overflow-hidden rounded-2xl md:rounded-[28px]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(80%_55%_at_8%_0%,rgba(0,200,83,0.14)_0%,rgba(0,200,83,0)_60%),radial-gradient(70%_60%_at_96%_4%,rgba(41,98,255,0.1)_0%,rgba(41,98,255,0)_66%),linear-gradient(180deg,#ffffff_0%,#fbfcfd_100%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.045] [background-size:22px_22px] [background-image:linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)]" />

      <div className="relative rounded-2xl md:rounded-[28px] ring-1 ring-black/[0.08] bg-card/80 backdrop-blur-sm p-3.5 md:p-6">
        <div className="flex flex-col gap-5 md:gap-6">
          <AgentListBreadcrumbs countryCode={countryCode} location={location} />

          <div>
            <h1
              className="text-[var(--venturu-black)] leading-tight tracking-tight"
              style={{
                fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                fontWeight: 700,
                letterSpacing: "-0.025em",
              }}
            >
              {formattedLocation
                ? `Business Brokers in ${formattedLocation}`
                : "Business Brokers"}
            </h1>
            {data && (
              <p className="font-mono text-sm text-[var(--venturu-gray-500)] mt-1">
                {data.total} verified broker{data.total !== 1 ? "s" : ""}
              </p>
            )}
          </div>

          <AgentListFilters />

          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <AgentListItemSkeleton key={`skel-${i}`} />
              ))}
            </div>
          )}

          {!isLoading && data && visibleAgents.length === 0 && (
            <AgentListZeroDataState countryCode={countryCode} />
          )}

          {!isLoading && data && visibleAgents.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleAgents.map((agent) => (
                  <AgentListItem key={agent.id} agent={agent} />
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2">
                <p className="text-xs font-mono text-[var(--venturu-gray-500)] text-center sm:text-left">
                  Showing {showingStart}–{showingEnd} of {data.total} broker
                  {data.total !== 1 ? "s" : ""}
                </p>
                <Pagination
                  data={data}
                  onPageChange={setPage}
                  isLoading={isFetching}
                  getPageHref={
                    basePath
                      ? (target) => {
                          const params = getAgentSearchParams(
                            filters,
                            new URLSearchParams(searchParams.toString()),
                          );
                          params.delete("page");
                          const query = params.toString();
                          return `${pagePath(basePath, target)}${query ? `?${query}` : ""}`;
                        }
                      : undefined
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

AgentListView.displayName = "AgentListView";
